// Row model shared by every estimating-database page.
//
// The /data service (curlight routes/data.js) answers every collection with
// rows shaped { default, average, company, oneBuild, craftsman }:
//   default  - BidLight's master data (OrganizationId "default")
//   company  - the organisation's own row
//   average  - the cross-company average
// and marks a row `fallback: true` when it only matched a looser filter
// (another version, no city, ...).
//
// The desktop database editor (BaseDatabaseModel.InitializeCompanyData)
// shows ONE record per row, picked by two switches, and this module is a
// port of that pick so both clients show the same thing:
//
//   scope "company"  - the organisation's own, non-fallback rows.
//   scope "bidlight" - the template: BidLight / average data, with the id
//                      cleared, so saving it COPIES it into the company.
//
// "master" is the BidLight admin looking at BidLight's own organisation: for
// them the company data IS the default data.

export const SCOPE_COMPANY = "company";
export const SCOPE_BIDLIGHT = "bidlight";

export const IMPORT_MODES = {
  ADD_NEW_OVERWRITE_EXISTING: "AddNewOverWriteExisting",
  ADD_NEW_EXCLUDE_EXISTING: "AddNewExcludeExisting",
  REMOVE_ALL_IMPORT_NEW: "RemoveAllImportNew",
  EXCLUDE_NEW_OVERWRITE_EXISTING: "ExcludeNewOverWriteExisting",
};

export const IMPORT_MODE_LABELS = {
  [IMPORT_MODES.ADD_NEW_OVERWRITE_EXISTING]: "Add new rows and overwrite existing rows",
  [IMPORT_MODES.ADD_NEW_EXCLUDE_EXISTING]: "Add new rows and keep existing rows as they are",
  [IMPORT_MODES.REMOVE_ALL_IMPORT_NEW]: "Replace everything with the file (rows missing from the file are deleted)",
  [IMPORT_MODES.EXCLUDE_NEW_OVERWRITE_EXISTING]: "Only overwrite existing rows, ignore new ones",
};

// DatabaseTabPage.xaml + DatabaseTabViewModel.ShowEditDeleteOption /
// ShowAddCloneOption: the BidLight (template) scope hides add, clone, edit,
// delete and import; a BidLight admin in the company scope gets edit and
// delete but not add or clone.
export function toolbarOptions({ bidlight, isAdmin }) {
  return {
    showEditDelete: !bidlight,
    showAddClone: !bidlight && !isAdmin,
    showImport: !bidlight,
  };
}

export function isMasterView(userInfo, selectedCompanyId) {
  if (!userInfo || !userInfo.admin) return false;
  return !selectedCompanyId || selectedCompanyId === userInfo.company;
}

// EstimationResponse.DatabaseSelect(): the comparison ("database") value.
export const databaseSelect = (row) =>
  (row && (row.average || row.default || row.company)) || null;

// BaseDatabaseModel.InitializeCompanyData, per row.
export function pickScopeRecord(row, { master, scope }) {
  if (!row) return null;
  const bidlight = scope === SCOPE_BIDLIGHT;
  let item = null;
  if (master) {
    if (bidlight) {
      item = row.default && row.default.fallback ? row.default : row.average;
    } else {
      item = row.default && !row.default.fallback ? row.default : null;
    }
  } else if (bidlight) {
    item =
      row.company && row.company.fallback
        ? row.company
        : row.average || row.default;
  } else {
    item = row.company && !row.company.fallback ? row.company : null;
  }
  if (!item) return null;

  const rec = { ...item };
  if (bidlight) delete rec._id;

  let fallback = !!rec.fallback;
  if (master) {
    if (!row.default && item === row.average) fallback = true;
  } else if (!row.company && item === row.average) {
    fallback = true;
  }
  rec.fallback = fallback;

  // Rows the desktop tracks as "auto adding": they only exist once saved.
  const autoNew = master ? fallback : !rec._id;
  return { rec, db: databaseSelect(row), autoNew };
}

// DataLoadedFirstTime: the organisation has no rows of its own yet.
export function isFirstLoad(rows, master) {
  return !(rows || []).some((r) =>
    master
      ? r && r.default && !r.default.fallback
      : r && r.company && !r.company.fallback
  );
}

let keySeq = 0;
export const newKey = (prefix = "row") => `${prefix}_${Date.now().toString(36)}_${++keySeq}`;

// Server rows -> page items { key, rec, db, orig, status, selected }.
export function buildItems(rows, { master, scope, fromWire = (w) => w }) {
  const items = [];
  (rows || []).forEach((row) => {
    const picked = pickScopeRecord(row, { master, scope });
    if (!picked) return;
    const rec = fromWire(picked.rec);
    items.push({
      key: rec._id || newKey(),
      rec,
      db: picked.db ? fromWire({ ...picked.db }) : null,
      orig: { ...rec },
      status: picked.autoNew ? "new" : "clean",
      selected: false,
    });
  });
  return items;
}

const sameValue = (a, b) => {
  if (Array.isArray(a) || Array.isArray(b)) {
    return JSON.stringify(a || []) === JSON.stringify(b || []);
  }
  if (a === b) return true;
  if ((a === null || a === undefined || a === "") && (b === null || b === undefined || b === "")) {
    return true;
  }
  return false;
};

export function isRecordChanged(rec, orig, fields) {
  return fields.some((f) => !sameValue(rec[f], orig ? orig[f] : undefined));
}

export function applyEdit(items, key, patch, fields) {
  return items.map((it) => {
    if (it.key !== key) return it;
    const rec = { ...it.rec, ...patch };
    let status = it.status;
    if (status !== "new") {
      status = isRecordChanged(rec, it.orig, fields) ? "dirty" : "clean";
    }
    return { ...it, rec, status };
  });
}

// A row added through the dialog starts ticked (AddNewItem sets
// SelectedForSave); clones and imported rows do not.
export function addItem(items, rec, { selected = true } = {}) {
  return [
    ...items,
    { key: newKey("new"), rec: { ...rec }, db: null, orig: null, status: "new", selected },
  ];
}

// Deleting in the BidLight scope only forgets the row (the desktop
// UnTracks it); in the company scope a saved row is deleted on the server.
export function removeItems(items, keys, scope) {
  const gone = items.filter((it) => keys.includes(it.key));
  const deleted =
    scope === SCOPE_COMPANY
      ? gone.filter((it) => it.rec._id && !it.existing).map((it) => ({ ...it.rec }))
      : [];
  return { items: items.filter((it) => !keys.includes(it.key)), deleted };
}

export function setSelected(items, keys, selected) {
  return items.map((it) => (keys.includes(it.key) ? { ...it, selected } : it));
}

// What a save sends. Company scope saves every pending change; the BidLight
// scope saves only what is ticked, which is the desktop's "Move Selection to
// Company Database".
// BaseDatabaseModel.SaveToApi: new rows are POSTed and edited rows PUT
// only when ticked (SelectedForSave), except on the company-wide pages
// (GenericForAllCompany); deletions always go. `leaving` counts every
// change regardless of ticks (GetDataChangedCount(isLeaving: true)), which
// is what "discard unsaved changes?" asks about.
export function planSave(items, deleted, { scope, generic = false, leaving = false }) {
  const ticked = (it) => generic || leaving || it.selected;
  if (scope === SCOPE_BIDLIGHT) {
    return {
      post: items.filter((it) => (leaving || it.selected) && !it.existing).map((it) => it.rec),
      put: [],
      del: [],
    };
  }
  return {
    post: items.filter((it) => it.status === "new" && !it.existing && ticked(it)).map((it) => it.rec),
    put: items.filter((it) => it.status === "dirty" && it.rec._id && ticked(it)).map((it) => it.rec),
    del: deleted || [],
  };
}

// LaborDatabaseViewModel.BuildTree / ToolsDatabaseViewModel.BuildTree: a
// child (labour, tool) hangs under the parent (crew, tool type) whose Code
// equals its parent field. When that parent is not in the scope's list but
// the company has a row for it (row.Company set, not picked for the scope,
// DatabaseSelect present), that company row is borrowed as the parent
// (ExistingDataInDB, never saved). A child with no parent either way is
// not shown.
export function attachChildren(parentItems, childItems, { parentRows, parentKeyOf, master, scope, fromWire = (w) => w }) {
  const byCode = new Map();
  (parentItems || []).forEach((p) => {
    const code = String(p.rec.Code);
    if (!byCode.has(code)) byCode.set(code, { parent: p, children: [] });
  });
  const borrowed = new Map();
  (parentRows || []).forEach((row) => {
    if (!row || !row.company || !databaseSelect(row)) return;
    if (pickScopeRecord(row, { master, scope })) return;
    const code = String(row.company.Code);
    if (!borrowed.has(code)) borrowed.set(code, row.company);
  });
  const nodes = [...byCode.values()];
  const dropped = [];
  (childItems || []).forEach((c) => {
    const code = String(parentKeyOf(c.rec) === undefined || parentKeyOf(c.rec) === null ? "" : parentKeyOf(c.rec));
    if (!code) {
      dropped.push(c);
      return;
    }
    let node = byCode.get(code);
    if (!node && borrowed.has(code)) {
      const rec = fromWire({ ...borrowed.get(code) });
      node = {
        parent: { key: `existing_${code}`, rec, db: null, orig: { ...rec }, status: "clean", selected: false, existing: true },
        children: [],
      };
      byCode.set(code, node);
      nodes.push(node);
    }
    if (node) node.children.push(c);
    else dropped.push(c);
  });
  return { nodes, dropped };
}

const stripIdentity = (rec) => {
  const out = { ...rec };
  delete out._id;
  delete out.fallback;
  delete out.isPublic;
  if (Array.isArray(out.Awp)) out.Awp = [...out.Awp];
  return out;
};

// LaborDatabaseViewModel.CloneItem (crew) / ToolsDatabaseViewModel.CloneItem
// (tool type): a copy of the parent under the next top code
// (GetNewCrewCode / GetNewToolGroupCode) with copies of its children under
// "<new code>.<n>" (GetNewLaborCode / GetNewToolCode). `parentField` is the
// child's link to the parent ("Crew" / "Group").
export function cloneParentWithChildren(parentRec, childRecs, { parentCodes, parentField }) {
  const code = nextTopCode(parentCodes);
  const parent = { ...stripIdentity(parentRec), Code: code };
  const children = [];
  (childRecs || []).forEach((c) => {
    const childCode = nextChildCode(code, children.map((x) => x.Code));
    children.push({ ...stripIdentity(c), Code: childCode, [parentField]: code });
  });
  return { parent, children };
}

// Cloning one labour / tool: same parent, next child code.
export function cloneChild(childRec, siblingCodes, { parentField }) {
  const parentCode = childRec[parentField];
  return { ...stripIdentity(childRec), Code: nextChildCode(parentCode, siblingCodes) };
}

// EstimationItemViewModel.Index: int.TryParse(Code), 0 when it is not a
// number. The desktop grids sort ascending on it; equal keys keep the
// server order.
export const codeIndex = (code) => {
  const s = String(code === undefined || code === null ? "" : code).trim();
  if (!/^[+-]?\d+$/.test(s)) return 0;
  const n = parseInt(s, 10);
  return n > 2147483647 || n < -2147483648 ? 0 : n;
};

export function sortByIndex(items) {
  return (items || [])
    .map((it, i) => ({ it, i, k: codeIndex(it.rec && it.rec.Code) }))
    .sort((a, b) => a.k - b.k || a.i - b.i)
    .map((x) => x.it);
}

// LaborDatabaseViewModel.BuildTree: crews.OrderBy(x => x.Code), the .NET
// culture string order ("10" before "2").
export const compareCodeText = (a, b) =>
  String(a === undefined || a === null ? "" : a).localeCompare(String(b === undefined || b === null ? "" : b));

// BaseDatabaseModel.CloneItem -> CopyToNew: the row's editable values in a
// new record; no id, the server gives it a code.
export function cloneDraft(rec, fields) {
  const out = {};
  (fields || []).forEach((f) => {
    if (f.type === "readonly") return;
    if (rec && rec[f.key] !== undefined) out[f.key] = Array.isArray(rec[f.key]) ? [...rec[f.key]] : rec[f.key];
  });
  return out;
}

// Applies a page's metric rewrite to the rows a save would send: a row
// whose value changes becomes an edit (the desktop's in-memory conversion
// fires IsDirty). Rows already new stay new.
export function withMetricRewrite(items, rewrite) {
  if (!rewrite) return items;
  return (items || []).map((it) => {
    const next = rewrite(it.rec);
    if (!next) return it;
    return { ...it, rec: next, status: it.status === "clean" ? "dirty" : it.status };
  });
}

// The rows "Save to other version" / "Save to default" send: the ticked
// ones only (SelectedForSave).
export const tickedRecords = (items) => (items || []).filter((it) => it.selected).map((it) => it.rec);

// BaseDatabaseModel.GetDataChangedCount: new and edited rows count only
// when ticked, except on the pages shared by the whole company
// (GenericForAllCompany: AWP, labor types, project types, material
// classes); deletions always count in the company scope.
export function changedCountForCopy(items, deleted, { scope, generic = false } = {}) {
  const changed = (items || []).filter(
    (it) => it.status !== "clean" && !it.existing && (generic || it.selected)
  ).length;
  return changed + (scope === SCOPE_COMPANY ? (deleted || []).length : 0);
}

export function pendingCount(items, deleted, scope) {
  const p = planSave(items, deleted, { scope, leaving: true });
  return p.post.length + p.put.length + p.del.length;
}

// The version a page is filtered by. Every endpoint is read and written
// with these, exactly as DatabaseTabViewModel.GetData sends them.
//
// DatabaseTabViewModel.Version setter resolves each version field through
// a lookup list first and the filter is the resolved item's key:
//   Country     Countries.FirstOrDefault(Iso3 == version.Country)
//   State       Country.States.FirstOrDefault(StateCode == version.State)
//   City        State.Cities.FirstOrDefault(Name == version.City)
//   LaborType   LaborTypesUtil.Items.FirstOrDefault(APIUrlIdentifier == ..)
//   ProjectType ProjectTypesUtil.Items.FirstOrDefault(APIUrlIdentifier == ..)
// A value with no match becomes null and is left out of the GET and of new
// rows (real version "2704" stores LaborType "Open Shop", which is no
// labour type's identifier, so the desktop sends no LaborType at all).
// `lookups` = { countries, projectTypes, laborTypes }; without it the raw
// values are used.
const cleanValue = (v) => (v === null || v === undefined || v === "" || v === "null" ? undefined : v);

export function resolveVersionFields(template, lookups) {
  const t = template || {};
  if (!lookups) {
    return {
      Country: cleanValue(t.Country),
      State: cleanValue(t.State),
      City: cleanValue(t.City),
      ProjectType: cleanValue(t.ProjectType),
      LaborType: cleanValue(t.LaborType),
    };
  }
  const type = (list, id) => ((list || []).find((x) => x && x.APIUrlIdentifier === id) || {}).APIUrlIdentifier;
  // The country list could not be read: keep the stored place rather than
  // dropping it.
  if (!Array.isArray(lookups.countries)) {
    return {
      Country: cleanValue(t.Country),
      State: cleanValue(t.State),
      City: cleanValue(t.City),
      ProjectType: cleanValue(type(lookups.projectTypes, t.ProjectType)),
      LaborType: cleanValue(type(lookups.laborTypes, t.LaborType)),
    };
  }
  const country = lookups.countries.find((c) => c && c.iso3 === t.Country) || null;
  const state = country ? (country.states || []).find((s) => s && s.state_code === t.State) || null : null;
  const city = state ? (state.cities || []).find((c) => c && c.name === t.City) || null : null;
  return {
    Country: country ? cleanValue(country.iso3) : undefined,
    State: state ? cleanValue(state.state_code) : undefined,
    City: city ? cleanValue(city.name) : undefined,
    ProjectType: cleanValue(type(lookups.projectTypes, t.ProjectType)),
    LaborType: cleanValue(type(lookups.laborTypes, t.LaborType)),
  };
}

const dropEmpty = (out) => {
  Object.keys(out).forEach((k) => out[k] === undefined && delete out[k]);
  return out;
};

export function versionFilters(template, lookups) {
  const t = template || {};
  return dropEmpty({ ...resolveVersionFields(t, lookups), Version: cleanValue(t.Code) });
}

// SaveToOtherVersionAPI: the target version's own fields, as stored, each
// one falling back to the loaded filter when the target has none
// (versionInfo?.CountryCode ?? LoadCountry?.Iso3, ...).
export function otherVersionFilters(target, loaded) {
  const t = target || {};
  const l = loaded || {};
  return dropEmpty({
    Country: cleanValue(t.Country) || l.Country,
    State: cleanValue(t.State) || l.State,
    City: cleanValue(t.City) || l.City,
    ProjectType: cleanValue(t.ProjectType) || l.ProjectType,
    LaborType: cleanValue(t.LaborType) || l.LaborType,
    Version: cleanValue(t.Code) || l.Version,
  });
}

// VersionUtil.Responses with AlwaysSelectCompanyData: the BidLight admin on
// BidLight's own organisation takes Default ?? Company ?? Average; anybody
// else Company ?? Average (a row with only BidLight's default is not one of
// the company's versions).
// IsCompany: true for the admin, else true only for the company's own row
// (an Average row is BidLight's version, not yet the company's).
export const templateOf = (row, adminOfOrg = false) => {
  if (!row) return null;
  const rec = adminOfOrg ? row.default || row.company || row.average || null : row.company || row.average || null;
  if (!rec) return null;
  return { ...rec, isCompany: adminOfOrg ? true : rec === row.company };
};

// ---- versions: new version, company copy (DatabaseTabViewModel) --------

export const PROTOCOLS = ["RevitUniFormat", "CSIUniFormat", "MasterFormat", "OmniClass", "TeslaMasterFormat"];
export const COST_DATABASES = ["BidLight", "Company", "OneBuild", "Craftsman"];
export const MEASUREMENTS = ["Imperial", "Metric"];

// LocationService.GetCurrencies: each country's currency, first one per name.
export function currencyList(countries) {
  const seen = new Set();
  const out = [];
  (countries || []).forEach((c) => {
    const name = c && c.currency;
    if (!name || seen.has(name)) return;
    seen.add(name);
    out.push(name);
  });
  return out;
}

// DatabaseTabViewModel.Version setter: what the filter panel holds for a
// version (each field resolved through its list; unknown values become
// empty; Measurements by case-sensitive Enum.TryParse, else Imperial;
// Protocol and DatabaseSource likewise with their defaults).
export function versionPanelFields(template, { countries, projectTypes, laborTypes }) {
  const t = template || {};
  const resolved = resolveVersionFields(t, { countries: Array.isArray(countries) ? countries : [], projectTypes, laborTypes });
  const currencies = currencyList(countries);
  return {
    VersionCode: t.Code || "",
    Country: resolved.Country || null,
    State: resolved.State || null,
    City: resolved.City || null,
    LaborType: resolved.LaborType || null,
    ProjectType: resolved.ProjectType || null,
    Currency: currencies.includes(t.Currency) ? t.Currency : null,
    Measurements: MEASUREMENTS.includes(t.Measurements) ? t.Measurements : "Imperial",
    Protocol: PROTOCOLS.includes(t.Protocol) ? t.Protocol : "RevitUniFormat",
    DatabaseSource: COST_DATABASES.includes(t.DatabaseSource) ? t.DatabaseSource : "Company",
  };
}

// ExecuteAddVersionCommand: a new version starts from an empty panel (the
// Version setter with null).
export const emptyVersionPanel = (code = "") => ({
  VersionCode: code,
  Country: null,
  State: null,
  City: null,
  LaborType: null,
  ProjectType: null,
  Currency: null,
  Measurements: "Imperial",
  Protocol: "RevitUniFormat",
  DatabaseSource: "Company",
});

// DatabaseTabViewModel.AddNewVersion + VersionUtil.Add checks, in order.
// Returns the message the desktop shows, or null.
export function validateNewVersion(panel, templates) {
  const p = panel || {};
  if (!p.VersionCode || !String(p.VersionCode).trim()) return "Version code can't be empty.";
  if (!p.Country) return "Country can't be empty.";
  if (!p.Currency) return "Currency can't be empty.";
  const code = String(p.VersionCode).toLowerCase();
  const existing = (templates || []).find((t) => t && t.Code !== undefined && t.Code !== null && String(t.Code).toLowerCase() === code);
  if (existing && existing.isCompany) return "Version with the same name already exist. Please use different version name.";
  return null;
}

// VersionUtil.Add -> NewVersionRequest (POST data/versions, Override=False).
export function newVersionRequest(panel, organizationId) {
  const p = panel || {};
  const body = {
    Code: p.VersionCode,
    Version: p.VersionCode,
    Country: p.Country,
    State: p.State,
    City: p.City,
    LaborType: p.LaborType,
    ProjectType: p.ProjectType,
    Currency: p.Currency,
    DatabaseSource: p.DatabaseSource,
    Measurements: p.Measurements,
    Protocol: p.Protocol,
    OrganizationId: organizationId,
  };
  Object.keys(body).forEach((k) => (body[k] === null || body[k] === undefined) && delete body[k]);
  return body;
}

// VersionUtil.Add on success: the same code (any case) is replaced by the
// new company version.
export function replaceTemplate(templates, created) {
  const code = String(created.Code).toLowerCase();
  return [
    ...(templates || []).filter((t) => !(t && t.Code !== undefined && t.Code !== null && String(t.Code).toLowerCase() === code)),
    { ...created, isCompany: true },
  ];
}

// ExecuteLoadItems: a version is only loaded with a country and a currency.
export function loadBlocker(template, { countries }) {
  if (!template) return "Version can't be empty.";
  if (!Array.isArray(countries) || !countries.length) return null;
  const panel = versionPanelFields(template, { countries, projectTypes: [], laborTypes: [] });
  if (!panel.Country) return "Country can't be empty.";
  if (!panel.Currency) return "Currency can't be empty.";
  return null;
}

// POST body: NewModelRequest = the record + the version filters + the
// organisation. isPublic only goes out with a Version (CanSerializeIsPublic).
export function toNewRequest(rec, toWire, filters, organizationId) {
  const body = { ...toWire(rec), ...filters, OrganizationId: organizationId };
  delete body._id;
  delete body.fallback;
  if (filters && filters.Version) {
    body.isPublic = !!rec.isPublic;
  } else {
    delete body.isPublic;
  }
  return body;
}

// PUT body: the response model (id + fields). The server re-stamps the
// organisation itself.
export function toUpdateRequest(rec, toWire) {
  const body = { ...toWire(rec), _id: rec._id };
  delete body.fallback;
  if (rec.isPublic !== undefined && rec.isPublic !== null) body.isPublic = !!rec.isPublic;
  return body;
}

// BaseDatabaseModel.CreateAPIUrlIdentifier: drop spaces, lower-case the
// first letter ("Heavy Civil" -> "heavyCivil").
export function createApiUrlIdentifier(name) {
  const s = String(name || "").replace(/ /g, "");
  if (!s) return s;
  return s.charAt(0).toLowerCase() + s.substring(1);
}

const parseIntLikeDotNet = (s) => {
  const m = /^\s*[+-]?\d+\s*$/.test(String(s === undefined || s === null ? "" : s));
  return m ? parseInt(String(s).trim(), 10) : 0;
};

// GetNewCrewCode / GetNewToolGroupCode: max integer code + 1, "1" when none.
export function nextTopCode(codes) {
  const list = (codes || []).map(parseIntLikeDotNet);
  if (!list.length) return "1";
  return String(Math.max(...list) + 1);
}

// GetNewLaborCode / GetNewToolCode: "<parent>.<max child suffix + 1>".
export function nextChildCode(parentCode, childCodes) {
  if (parentCode === null || parentCode === undefined) return "1.1";
  const suffixes = [];
  (childCodes || []).forEach((c) => {
    if (!c) return;
    const parts = String(c).split(".");
    if (parts.length !== 2) return;
    suffixes.push(parseIntLikeDotNet(parts[1].trim()));
  });
  if (suffixes.length) return `${parentCode}.${Math.max(...suffixes) + 1}`;
  if (String(parentCode) !== "") return `${parentCode}.1`;
  return "1.1";
}

// Field validation shared by the edit modal, inline edits and imports.
export function validateRecord(rec, fields, otherRecs = []) {
  const errors = {};
  fields.forEach((f) => {
    const v = rec[f.key];
    const empty =
      v === null || v === undefined || (typeof v === "string" && !v.trim()) || (Array.isArray(v) && !v.length);
    if (f.required && empty) {
      errors[f.key] = `${f.label} is required`;
      return;
    }
    if (f.type === "number" && !empty && !Number.isFinite(Number(v))) {
      errors[f.key] = `${f.label} must be a number`;
      return;
    }
    if (f.min !== undefined && !empty && Number(v) < f.min) {
      errors[f.key] = `${f.label} can't be less than ${f.min}`;
      return;
    }
    if (f.positive && !empty && Number(v) <= 0) {
      errors[f.key] = `${f.label} must be greater than zero`;
      return;
    }
    if (f.unique && !empty) {
      const norm = (x) => String(x === undefined || x === null ? "" : x).trim().toLowerCase();
      if (otherRecs.some((o) => norm(o[f.key]) === norm(v))) {
        errors[f.key] = `${f.label} "${v}" already exists`;
      }
    }
  });
  return errors;
}

// Desktop import (Materials/Labor/Tools ImportData): rows are matched on a
// key; the four modes decide what happens to new and existing rows.
export function importRecords(items, records, { mode, keyOf, scope, fields }) {
  const byKey = new Map(items.map((it) => [String(keyOf(it.rec)), it]));
  const seen = new Set();
  let next = items.slice();
  records.forEach((rec) => {
    const k = String(keyOf(rec));
    seen.add(k);
    const existing = byKey.get(k);
    if (existing) {
      if (mode === IMPORT_MODES.ADD_NEW_EXCLUDE_EXISTING) return;
      next = applyEdit(next, existing.key, rec, fields);
    } else if (mode !== IMPORT_MODES.EXCLUDE_NEW_OVERWRITE_EXISTING) {
      next = addItem(next, rec, { selected: false });
    }
  });
  let deleted = [];
  if (mode === IMPORT_MODES.REMOVE_ALL_IMPORT_NEW) {
    const drop = next.filter((it) => !seen.has(String(keyOf(it.rec)))).map((it) => it.key);
    const res = removeItems(next, drop, scope);
    next = res.items;
    deleted = res.deleted;
  }
  return { items: next, deleted };
}

export function matchesSearch(rec, searchFields, text) {
  if (!text) return true;
  const hay = searchFields.map((f) => (rec[f] === undefined || rec[f] === null ? "" : String(rec[f]))).join("");
  return hay.toLowerCase().includes(String(text).toLowerCase());
}
